import { VideoData } from '@lumen5/luminary';

import {
    assetContainerModelIsAudio,
    assetContainerModelIsBackground,
    type FileMetadata,
    type FileWithMetadata,
    type UploadFailedCallback,
    type UploadMetadataUpdatedCallback,
    type UploadOptions,
    type UploadProgressCallback,
    type UploadSuccessCallback,
} from '../data/uploads/uploads-types';

import type { AssetContainerModel, AssetMetadataRequest, UploadUrlsRequest } from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    AssetTypes,
    AsyncOperationMessageTypeEnum,
    AsyncOperationNameEnum,
    AudioDurationSeconds,
    BackgroundVideoDurationSeconds,
    SourceEnum,
    UploadFileSizeRequirements,
    UploadFileSizeRequirementsPretty,
    UploadUrlsRequestTypeEnum,
} from 'js/src/client';
import {
    isAudioMimeType,
    isImageMimeType,
    isVideoMimeType,
    slideBackgroundModelIsImageBackgroundModel,
    slideBackgroundModelIsVideoBackgroundModel,
} from 'js/src/data/media';
import { getBackgroundVideoDurationSecondsMax } from 'js/src/data/media/media-types';
import type { ApiError, FileFormat } from 'js/src/libs/consts';
import {
    getDisplayNamesForFileFormats,
    getMimeTypesForFileFormats,
    getUploadableFormatsMessage,
    getUserUploadableFileFormats,
    getValidAudioFileFormats,
    getValidDocumentFileFormats,
    getValidFontFileFormats,
    getValidIconFileFormats,
    getValidImageFileFormats,
    getValidVideoContainerFileFormats,
    getValidVideoFileFormats,
    isNotUndefined,
} from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { getFileNameFromUploadUrl } from 'js/src/libs/gcs-url';
import { type AugmentedFile, getFileMimeType } from 'js/src/libs/native-file-handler';
import { isPollAsyncOperationStatusError } from 'js/src/libs/poll_async_operation';
import { getGlobalUploadNetworkManager } from 'js/src/libs/upload-network-manager';
import uuid from 'js/src/libs/uuid';
import { getBlobDuration, validateInfinityDuration } from 'js/src/libs/video-duration-tools';
import WebSocketClient from 'js/src/libs/websocket-client';
import ActivityProfiler from 'js/src/profiling/ActivityProfiler';
import {
    METRIC_UPLOAD_FAILED_NAME,
    METRIC_UPLOAD_STARTED_NAME,
    METRIC_UPLOAD_SUCCESS_NAME,
} from 'js/src/profiling/metrics';
import { ErrorTypes, isErrorObject, OperationAbortedError, UploadError } from 'js/src/types';

type FileChunk = {
    chunk: Blob;
    start: number;
    end: number;
};
type ChunkResult = {
    etag: string;
    partNumber: number;
};

const UPLOAD_VALIDATION_TIMEOUT_MS = 60 * 60 * 1000;

export const ERROR_MESSAGES = {
    DEFAULT: 'An unknown error occurred.',
    GENERIC_INVALID_UPLOAD_ERROR:
        "Oops, something went wrong. Try uploading your file again or contact our Support team if you're still having trouble.",
    INVALID_ICON_FILE: 'Oops, this is not a valid SVG or JSON file.',
    IMG_LOAD_ERROR: 'The image is not loading correctly, please ensure it is valid.',
    ONLY_IMAGES: 'Only .png, .jpg, .bmp, and .webp files can be uploaded.',
    INVALID_AUDIO_FILE: 'Oops! Your file must be an MP3, WAV, or M4A to upload.',
    INVALID_VISUAL_MEDIA_FILE: `Oops! Your file must be a ${getUploadableFormatsMessage([AssetTypes.IMAGE, AssetTypes.VIDEO])} to upload.`,
    FILE_SIZE_TOO_SMALL: `Please ensure your file is larger than ${UploadFileSizeRequirementsPretty.MIN_BYTES}.`,
    FILE_SIZE_TOO_BIG: `Please ensure your file is less than ${UploadFileSizeRequirementsPretty.MAX_BYTES}.`,
    IMG_DIMENSIONS_TOO_SMALL: (min_width: number, min_height: number) =>
        `Please ensure your image is at least ${min_width}px by ${min_height}px.`,
    IMG_DIMENSIONS_TOO_BIG: (max_width: number, max_height: number) =>
        `Please ensure your image is not more than ${max_width}px by ${max_height}px.`,
    VIDEO_DIMENSIONS_TOO_SMALL: (min_width: number, min_height: number) =>
        `Please ensure your video is at least ${min_width}px by ${min_height}px.`,
    VIDEO_DIMENSIONS_TOO_BIG: (max_width: number, max_height: number) =>
        `Please ensure your video is not more than ${max_width}px by ${max_height}px.`,
    VIDEO_DURATION_TOO_SHORT: `Please ensure your video is ${BackgroundVideoDurationSeconds.MIN} seconds or longer.`,
    VIDEO_DURATION_TOO_LONG: `Please ensure your video is not more than ${getBackgroundVideoDurationSecondsMax()} seconds long.`,
    AUDIO_DURATION_TOO_SHORT: `Please ensure your music is at least ${AudioDurationSeconds.MIN} seconds long.`,
    AUDIO_DURATION_TOO_LONG: `Oops, audio files must be shorter than ${AudioDurationSeconds.MAX} seconds long.`,
    FILE_LOAD_ERROR: 'The file is not loading correctly, please ensure it is valid.',
};

enum OtherRequestTypes {
    TRIGGERING_VALIDATION = 'TRIGGERING_VALIDATION',
    BASIC_UPLOAD_TO_GCS = 'BASIC_UPLOAD_TO_GCS',
    INITIALIZE_MULTIPART_UPLOAD_WITH_GCS = 'INITIALIZE_MULTIPART_UPLOAD_WITH_GCS',
    FINALIZE_MULTIPART_UPLOAD_WITH_GCS = 'FINALIZE_MULTIPART_UPLOAD_WITH_GCS',
    UPLOAD_CHUNK_TO_GCS = 'UPLOAD_CHUNK_TO_GCS',
}

type RequestTypes = UploadUrlsRequestTypeEnum | OtherRequestTypes;
type HandleableError =
    | ApiRequestError
    | GCSUploadError
    | FileError
    | TaskError
    | UploadError
    | OperationAbortedError;

// Share of an upload's reported progress for getting an upload URL and for sending the bytes.
// The rest (validation on the server) is a time-based estimate, not measured progress.
export const UPLOAD_URL_PROGRESS = 5.0;
export const GCS_UPLOAD_PROGRESS = 45.0;

class ApiRequestError extends UploadError {
    status: number;
    url: string;
    requestType: RequestTypes;

    constructor(apiError: ApiError, requestType: RequestTypes) {
        const body = apiError.body as { message?: unknown } | null | undefined;
        const errorMessage =
            typeof body?.message === 'string' && body.message
                ? body.message
                : ERROR_MESSAGES.GENERIC_INVALID_UPLOAD_ERROR;
        super(ErrorTypes.REQUEST_ERROR, errorMessage);
        this.status = apiError.status;
        this.url = apiError.url;
        this.requestType = requestType;
    }
}

/**
 * Error response structure:
 * https://cloud.google.com/storage/docs/json_api/v1/status-codes#errorformat
 */
class GCSUploadError extends UploadError {
    constructor(message: string) {
        super(ErrorTypes.REQUEST_ERROR, message);
    }
}

export class FileError extends UploadError {
    constructor(message: string) {
        super(ErrorTypes.FILE_ERROR, message);
        this.name = 'FileError';
    }
}

class TaskError extends UploadError {
    task_status: number;
    task_error_message: string;
    constructor(message: string, status: number, expectedError: boolean) {
        let errorMessage = ERROR_MESSAGES.GENERIC_INVALID_UPLOAD_ERROR;
        if (message) {
            errorMessage = message;
        }
        super(ErrorTypes.TASK_ERROR, errorMessage);
        this.task_status = status;
        this.task_error_message = message;
        this.expected_error = expectedError;
    }
}

enum UploadStageStatus {
    NOT_STARTED = 'n/a',
    SUCCESS = 'successful',
    ERROR = 'error',
}

type UploadSpeedTracker = {
    name: string;
    size: number;
    type: string;
    chunked: boolean;
    frontendValidationDuration: number;
    frontendValidationStatus: UploadStageStatus;
    temporaryUrlFetchDuration: number;
    temporaryUrlFetchStatus: UploadStageStatus;
    gcsUploadDuration: number;
    gcsUploadStatus: UploadStageStatus;
    validatingUploadDuration: number;
    validatingUploadStatus: UploadStageStatus;
    pollingResultDuration: number;
    pollingResultStatus: UploadStageStatus;
    totalDuration: DOMHighResTimeStamp;
};

// The success/failure events already carry the file name and their own `type`.
function uploadStageTimings({ name, type, ...timings }: UploadSpeedTracker) {
    return timings;
}

/**
 * Downscales an already-loaded image so it fits within the given bounds, returning
 * a new File. The original aspect ratio is preserved by using the smaller of the
 * two scale factors. PNG and WebP are kept as-is to avoid losing transparency;
 * any other source format is re-encoded as JPEG (q=0.92) for size/compatibility.
 *
 * The returned File mirrors the original's name and lastModified so downstream
 * naming and analytics stay consistent, and propagates the AugmentedFile-only
 * fields (relativePath, mimeTypeHint) since callers may rely on them.
 */
async function resizeImageToFit(
    originalFile: AugmentedFile,
    img: HTMLImageElement,
    maxWidth: number,
    maxHeight: number,
): Promise<AugmentedFile> {
    const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
    const targetWidth = Math.floor(img.width * scale);
    const targetHeight = Math.floor(img.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Failed to get canvas 2d context for image resize');
    }
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    const sourceMime = getFileMimeType(originalFile);
    const preservesAlpha = sourceMime === 'image/png' || sourceMime === 'image/webp';
    const outputMime = preservesAlpha ? sourceMime : 'image/jpeg';
    // canvas.toBlob ignores the quality arg for lossless formats, so we only set
    // it for JPEG output to make the intent obvious.
    const quality = outputMime === 'image/jpeg' ? 0.92 : undefined;

    const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, outputMime, quality);
    });
    if (!blob) {
        throw new Error('Canvas.toBlob returned null during image resize');
    }

    const resized = new File([blob], originalFile.name, {
        type: outputMime,
        lastModified: originalFile.lastModified,
    }) as AugmentedFile;
    resized.relativePath = originalFile.relativePath;
    resized.mimeTypeHint = originalFile.mimeTypeHint;
    return resized;
}

/**
 * If the uploaded files are to be used as assets later on, please use uploads-operations.ts - That class uses this class underneath
 * the hood, but also handles licensing/attribution/tags and will save the asset to the user's library.
 *
 * Use this class if you want something uploaded to GCS, but you have to handle how the generated asset will be saved.
 *
 * The contract for this file is it should take [{ file: AugmentedFile, metadata: FileMetadata }] and return (in the form of callback)
 * for each file 3 things: FileWithMetadata, new_asset, asset_type
 */
class UploadMediaController {
    file_list: FileWithMetadata[];
    asset_type: AssetContainerTypes;
    metadata: FileMetadata;
    progress_callback: UploadProgressCallback;
    upload_succeeded_callback: UploadSuccessCallback;
    upload_failed_callback: UploadFailedCallback;
    metadata_updated_callback: UploadMetadataUpdatedCallback;
    abort_controller: AbortController;
    add_to_user_library: boolean;
    min_width: number;
    min_height: number;
    max_width: number;
    max_height: number;
    only_images: boolean;
    session_id: ReturnType<typeof uuid>;
    progress_dict: Record<string, number>;
    #analyticsContext: Record<string, string | number | boolean>;

    #isElementUpload: boolean = false;

    // by default we consider uploads to be user uploads
    #isUserUpload: boolean = true;

    // When an image exceeds max dimensions, validation downscales it and stores
    // the resized blob here keyed by the original File reference. The original File
    // stays in callbacks/redux (which use reference equality for lookups), while
    // the GCS upload uses the resized blob.
    #resizedUploadBlobs: WeakMap<File, AugmentedFile> = new WeakMap();

    // consts
    UPLOAD_URL_PROGRESS: number;
    GCS_UPLOAD_PROGRESS: number;
    FILE_VALIDATION_PROGRESS: number;
    UPLOAD_SOURCE: number;
    CHUNK_SIZE: number;

    DEFAULT_FILE_METADATA: FileMetadata = {
        source: SourceEnum.SOURCE_USER,
        license: VideoData.USER_PRIVATE_LICENSE,
    };

    DEFAULT_UPLOAD_OPTIONS = {
        metadata: this.DEFAULT_FILE_METADATA,
        progress_callback: () => {},
        upload_succeeded_callback: () => {},
        upload_failed_callback: () => {},
        metadata_updated_callback: () => {},
        abort_controller: new AbortController(),
        add_to_user_library: true,
        min_width: 0,
        min_height: 0,
        max_width: Number.MAX_VALUE,
        max_height: Number.MAX_VALUE,
        only_images: false,
        analyticsContext: {},
    };

    /**
     * A utility that controls the full process to upload a file.
     * Just construct the object and call upload().
     * The params, passed in as the opts dictionary, accepts:
     * - file_list: the list of things to upload, taken directly out of an <input type="file"> element (required)
     * - asset_type: the type of asset to upload (required)
     * - metadata: the data to use to construct the ImageAsset/VideoAsset/AudioAsset in the db (optional)
     * - progress_callback: a callback to update a loading bar, passing in just the percentage complete (optional)
     * - upload_succeeded_callback: a callback to notify each time an upload succeeds (for each file), passing in the file obj, new asset and its type (optional)
     * - upload_failed_callback: a callback to notify each time an upload fails (for each file), passing in the file_obj and a failure message (optional)
     * - abort_controller: handles calls to abort the upload operation
     * - add_to_user_library: whether or not the item should be added to the user's media library (optional)
     * - min_width: the minimum width to enforce (optional)
     * - min_height: the minimum height to enforce (optional)
     * - max_width: the maximum width to enforce (optional)
     * - max_height: the maximum width to enforce (optional)
     * - only_images: only allow images to be uploaded through this controller (optional)
     */
    constructor(opts: UploadOptions) {
        this.file_list = opts.file_list;
        this.asset_type = opts.asset_type;
        const { is_element, ...rest } = {
            ...this.DEFAULT_FILE_METADATA,
            ...opts.metadata,
        };
        this.metadata = rest;
        this.#isElementUpload = is_element ?? false;
        this.#isUserUpload = opts.is_user_upload ?? true;
        this.progress_callback =
            opts.progress_callback ?? this.DEFAULT_UPLOAD_OPTIONS.progress_callback;
        this.upload_succeeded_callback =
            opts.upload_succeeded_callback ?? this.DEFAULT_UPLOAD_OPTIONS.upload_succeeded_callback;
        this.upload_failed_callback =
            opts.upload_failed_callback ?? this.DEFAULT_UPLOAD_OPTIONS.upload_failed_callback;
        this.metadata_updated_callback =
            opts.metadata_updated_callback ?? this.DEFAULT_UPLOAD_OPTIONS.metadata_updated_callback;
        this.abort_controller =
            opts.abort_controller ?? this.DEFAULT_UPLOAD_OPTIONS.abort_controller;
        this.add_to_user_library =
            opts.add_to_user_library ?? this.DEFAULT_UPLOAD_OPTIONS.add_to_user_library;
        this.min_width = opts.min_width ?? this.DEFAULT_UPLOAD_OPTIONS.min_width;
        this.min_height = opts.min_height ?? this.DEFAULT_UPLOAD_OPTIONS.min_height;
        this.max_width = opts.max_width ?? this.DEFAULT_UPLOAD_OPTIONS.max_width;
        this.max_height = opts.max_height ?? this.DEFAULT_UPLOAD_OPTIONS.max_height;
        this.only_images = opts.only_images ?? this.DEFAULT_UPLOAD_OPTIONS.only_images;
        this.#analyticsContext =
            opts.analyticsContext ?? this.DEFAULT_UPLOAD_OPTIONS.analyticsContext;
        this.session_id = opts.session_id || uuid();

        // a dict of progress {file_name: <int, percent complete>}
        this.progress_dict = {};

        // consts, used to make sure the right progress is being calculated for each file
        // these numbers should sum to 100 (representing 100% complete)
        this.UPLOAD_URL_PROGRESS = UPLOAD_URL_PROGRESS;
        this.GCS_UPLOAD_PROGRESS = GCS_UPLOAD_PROGRESS;
        this.FILE_VALIDATION_PROGRESS = 50.0 as const;
        this.UPLOAD_SOURCE = SourceEnum.SOURCE_USER;
        // minimum 5MB (GCS doesn't support smaller). Currently ~25MB.
        // Each chunk will send a request to the UploadUrls endpoint, and if the files are especially large
        // then we could trigger rate limiting. For example, if the user is uploading 5 x 2GB files, then
        // we will be sending 5 x 2GB / 25MB = 400 requests to the UploadUrls endpoint.
        // This needs to remain below the burst rate limit (currently set to 500 requests per minute).
        this.CHUNK_SIZE = 25 * 1e6;
    }

    upload() {
        /*
        file_list is a list of objects like this: [
            {
                name: <file name (no path, like: "my_picture.jpg")>,
                lastModified: <int>,
                lastModifiedDate: <Date obj>,
                size: <int>,
                type: <mime type, str, like: 'image/png',
                ... // some other, platform dependant properties
            }
        ]
        */
        this.file_list.map((fileWithMetadata) => {
            this._upload_file(fileWithMetadata);
        });
    }

    async _upload_file(fileWithMetadata: FileWithMetadata) {
        // Custom object to store how long each event takes for the final upload.
        const fileData = fileWithMetadata.file;
        let fileMetadata = fileWithMetadata.metadata;
        const uploadSpeedTracker: UploadSpeedTracker = {
            name: fileData.name,
            size: fileData.size,
            type: fileData.type,
            chunked: false,
            frontendValidationDuration: 0,
            frontendValidationStatus: UploadStageStatus.NOT_STARTED,
            temporaryUrlFetchDuration: 0,
            temporaryUrlFetchStatus: UploadStageStatus.NOT_STARTED,
            gcsUploadDuration: 0,
            gcsUploadStatus: UploadStageStatus.NOT_STARTED,
            validatingUploadDuration: 0,
            validatingUploadStatus: UploadStageStatus.NOT_STARTED,
            pollingResultDuration: 0,
            pollingResultStatus: UploadStageStatus.NOT_STARTED,
            totalDuration: 0,
        };

        // set the timer
        uploadSpeedTracker.totalDuration = performance.now();

        // set the progress for this file to 0
        this.progress_dict[fileData.name] = 0;
        this._trigger_progress_callback();

        /**
         * couple things happen when we upload, each step with its own error-handling logic (time to error logic)
         * 1. create an upload session with GCS, it returns the GCS url and a unique uuid (asyncOperationUuid) for the session
         * 2. upload the file to GCS, it returns the bucket url
         * 3. start a task in the backend for asset validation - then use asyncOperationUuid to poll for status
         * 4. await via polling the upload status endpoint - resolves with a MediaAsset
         */
        ActivityProfiler.inc(METRIC_UPLOAD_STARTED_NAME);

        const frontendValidationTracker = performance.now();
        try {
            const updatedMetadata = await this._is_valid_file(fileWithMetadata);
            if (updatedMetadata) {
                // Metadata was updated during validation, use the updated metadata
                fileMetadata = updatedMetadata;
            }
        } catch (e) {
            uploadSpeedTracker.frontendValidationDuration =
                performance.now() - frontendValidationTracker;
            uploadSpeedTracker.frontendValidationStatus = UploadStageStatus.ERROR;
            uploadSpeedTracker.totalDuration = performance.now() - uploadSpeedTracker.totalDuration;
            this.handleErrors(
                e as HandleableError,
                { file: fileData, metadata: fileMetadata },
                uploadSpeedTracker,
            );
            return;
        }

        uploadSpeedTracker.frontendValidationDuration =
            performance.now() - frontendValidationTracker;
        uploadSpeedTracker.frontendValidationStatus = UploadStageStatus.SUCCESS;

        // If validation downscaled the image, the resized blob is what we send to GCS.
        // The original File reference is what callers (and redux) keep using.
        const fileDataForUpload = this.#resizedUploadBlobs.get(fileData) ?? fileData;
        if (fileDataForUpload !== fileData) {
            uploadSpeedTracker.size = fileDataForUpload.size;
            uploadSpeedTracker.type = fileDataForUpload.type;
        }

        const temporaryUrlTimer = performance.now();

        let makeChunks, upload_url, asyncOperationUuid, signedBucket;
        try {
            ({
                upload_url,
                bucket: signedBucket,
                makeChunks,
                asyncOperationUuid,
            } = await this._get_upload_url(fileData));
        } catch (e) {
            uploadSpeedTracker.temporaryUrlFetchDuration = performance.now() - temporaryUrlTimer;
            uploadSpeedTracker.temporaryUrlFetchStatus = UploadStageStatus.ERROR;
            uploadSpeedTracker.totalDuration = performance.now() - uploadSpeedTracker.totalDuration;
            this.handleErrors(
                e as HandleableError,
                { file: fileData, metadata: fileMetadata },
                uploadSpeedTracker,
            );
            return;
        }

        uploadSpeedTracker.chunked = makeChunks;
        uploadSpeedTracker.temporaryUrlFetchDuration = performance.now() - temporaryUrlTimer;
        uploadSpeedTracker.temporaryUrlFetchStatus = UploadStageStatus.SUCCESS;

        // now that we have the upload url, we can upload to GCS
        const gcsUploadTimer = performance.now();
        // The bucket always comes from the signed-URL response, never from the upload
        // URL: the same-origin upload path rewrites the URL host (e.g.
        // lumen5.com/uploads/...), so it isn't the bucket. The GCS upload only tells
        // us the (possibly sanitized) file_name.
        const bucket = signedBucket;
        let file_name;
        try {
            ({ file_name } = await this._upload_file_to_gcs(
                fileDataForUpload,
                upload_url,
                makeChunks,
            ));
        } catch (e) {
            uploadSpeedTracker.gcsUploadDuration = performance.now() - gcsUploadTimer;
            uploadSpeedTracker.gcsUploadStatus = UploadStageStatus.ERROR;
            uploadSpeedTracker.totalDuration = performance.now() - uploadSpeedTracker.totalDuration;
            this.handleErrors(
                e as HandleableError,
                { file: fileData, metadata: fileMetadata },
                uploadSpeedTracker,
            );
            return;
        }

        // track duration and status
        uploadSpeedTracker.gcsUploadDuration = performance.now() - gcsUploadTimer;
        uploadSpeedTracker.gcsUploadStatus = UploadStageStatus.SUCCESS;

        await this._finalizeUploadedAsset({
            fileWithMetadata: { file: fileData, metadata: fileMetadata },
            bucket,
            file_name,
            asyncOperationUuid,
            uploadSpeedTracker,
        });
    }

    /**
     * Entry point for assets that are already in GCS at the time we hear about
     * them — the recording-uploader streams blobs to GCS as the user records,
     * so by the time we finalize there is nothing left to PUT. This method
     * skips the frontend-validation + GCS-upload phases of `_upload_file` and
     * runs only the post-GCS finalization (validation, polling, asset wiring).
     */
    async uploadPreuploaded(
        fileWithMetadata: FileWithMetadata,
        gcsRef: { bucket: string; file_name: string; asyncOperationUuid: string },
    ) {
        const fileData = fileWithMetadata.file;
        const uploadSpeedTracker: UploadSpeedTracker = {
            name: fileData.name,
            size: fileData.size,
            type: fileData.type,
            chunked: false,
            frontendValidationDuration: 0,
            frontendValidationStatus: UploadStageStatus.NOT_STARTED,
            temporaryUrlFetchDuration: 0,
            temporaryUrlFetchStatus: UploadStageStatus.NOT_STARTED,
            // Upload happened during recording — treat both as already succeeded.
            gcsUploadDuration: 0,
            gcsUploadStatus: UploadStageStatus.SUCCESS,
            validatingUploadDuration: 0,
            validatingUploadStatus: UploadStageStatus.NOT_STARTED,
            pollingResultDuration: 0,
            pollingResultStatus: UploadStageStatus.NOT_STARTED,
            totalDuration: performance.now(),
        };

        // Mirror the progress that `_upload_file` would have reached by this point so
        // the existing progress bar (URL + GCS = 50 %) is in the right place when
        // _poll_validation begins animating the validation phase.
        this.progress_dict[fileData.name] = this.UPLOAD_URL_PROGRESS + this.GCS_UPLOAD_PROGRESS;
        this._trigger_progress_callback();

        await this._finalizeUploadedAsset({
            fileWithMetadata,
            bucket: gcsRef.bucket,
            file_name: gcsRef.file_name,
            asyncOperationUuid: gcsRef.asyncOperationUuid,
            uploadSpeedTracker,
        });
    }

    /**
     * Runs steps 4–7 of an upload: trigger backend validation, wait for the resulting AssetContainerModel
     * over WebSocket, fire analytics + Redux callbacks. Called by `_upload_file` after the GCS upload
     * completes, and by the recording-uploader flow after a resumable upload finishes — the recording
     * path has already streamed the bytes to GCS during recording, so it skips straight to finalization.
     */
    async _finalizeUploadedAsset({
        fileWithMetadata,
        bucket,
        file_name,
        asyncOperationUuid,
        uploadSpeedTracker,
    }: {
        fileWithMetadata: FileWithMetadata;
        bucket: string;
        file_name: string;
        asyncOperationUuid: string;
        uploadSpeedTracker: UploadSpeedTracker;
    }) {
        const fileData = fileWithMetadata.file;
        const fileMetadata = fileWithMetadata.metadata;

        // now that the file is in GCS, we can validate it through our server.
        // Note: file_name could be different than fileData.name at this point (to avoid collisions on GCS).
        const validationTimer = performance.now();
        try {
            await this._trigger_validation(
                fileData,
                bucket,
                file_name,
                fileMetadata,
                asyncOperationUuid,
            );
        } catch (e) {
            uploadSpeedTracker.validatingUploadDuration = performance.now() - validationTimer;
            uploadSpeedTracker.validatingUploadStatus = UploadStageStatus.ERROR;
            uploadSpeedTracker.totalDuration = performance.now() - uploadSpeedTracker.totalDuration;
            this.handleErrors(e as HandleableError, fileWithMetadata, uploadSpeedTracker);
            return;
        }

        uploadSpeedTracker.validatingUploadDuration = performance.now() - validationTimer;
        uploadSpeedTracker.validatingUploadStatus = UploadStageStatus.SUCCESS;

        const pollingTracker = performance.now();
        let new_asset = null;
        try {
            new_asset = await this._poll_validation(asyncOperationUuid, fileData);
        } catch (e) {
            uploadSpeedTracker.pollingResultDuration = performance.now() - pollingTracker;
            uploadSpeedTracker.pollingResultStatus = UploadStageStatus.ERROR;
            uploadSpeedTracker.totalDuration = performance.now() - uploadSpeedTracker.totalDuration;
            this.handleErrors(e as HandleableError, fileWithMetadata, uploadSpeedTracker);
            return;
        }

        uploadSpeedTracker.pollingResultDuration = performance.now() - pollingTracker;
        uploadSpeedTracker.pollingResultStatus = UploadStageStatus.SUCCESS;
        uploadSpeedTracker.totalDuration = performance.now() - uploadSpeedTracker.totalDuration;

        this._mark_as_finished(fileWithMetadata, new_asset, uploadSpeedTracker, asyncOperationUuid);
    }

    _poll_validation(asyncOperationUuid: string, file_obj: AugmentedFile) {
        return new Promise<AssetContainerModel>(async (resolve, reject) => {
            // we expect that 90% of validation-of-video tasks will complete in ~20 seconds
            const PREDICTED_TIME_SECONDS = 20;
            const startTime = performance.now();
            const intervalId = window.setInterval(() => {
                // https://mixpanel.com/s/1dvVw0
                const timePassedSeconds = (performance.now() - startTime) / 1000.0;
                let progessPercent = timePassedSeconds / PREDICTED_TIME_SECONDS;

                // don't allow the progress to get all the way to 100%.
                // if validation is taking longer than we expect, we shouldn't allow the progress bar to finish
                if (progessPercent > 0.95) {
                    progessPercent = 0.95;
                }
                this.progress_dict[file_obj.name] =
                    this.UPLOAD_URL_PROGRESS +
                    this.GCS_UPLOAD_PROGRESS +
                    this.FILE_VALIDATION_PROGRESS * progessPercent;
                this._trigger_progress_callback();
            }, 300);

            try {
                const ws = WebSocketClient.getInstance();
                const new_asset = await ws.waitForMessage<AssetContainerModel>(
                    asyncOperationUuid,
                    AsyncOperationMessageTypeEnum.ASYNC_OPERATION_RESULT,
                    AsyncOperationNameEnum.GET_SERIALIZED_ASSET_DATA,
                    {
                        abortController: this.abort_controller,
                        timeoutMs: UPLOAD_VALIDATION_TIMEOUT_MS,
                    },
                );
                window.clearInterval(intervalId);
                resolve(new_asset);
            } catch (e) {
                window.clearInterval(intervalId);
                if (isErrorObject(e) && isPollAsyncOperationStatusError(e.error)) {
                    reject(
                        new TaskError(e.error.errorThrown, e.error.status, e.error.expectedError),
                    );
                } else if (isErrorObject(e) && e.error instanceof OperationAbortedError) {
                    reject(e.error);
                } else if (isErrorObject(e) && e.error.message) {
                    reject(new TaskError(e.error.message, -1, false));
                } else {
                    reject(e);
                }
            }
        });
    }

    _mark_as_finished(
        file_with_metadata: FileWithMetadata,
        new_asset: AssetContainerModel,
        uploadSpeedTracker: UploadSpeedTracker,
        asyncOperationUuid: string,
    ) {
        const file_obj = file_with_metadata.file;
        this.progress_dict[file_obj.name] = 100;
        this._trigger_progress_callback();
        this.upload_succeeded_callback(
            file_with_metadata,
            new_asset,
            new_asset.type,
            asyncOperationUuid,
        );
        const eventData: Record<string, unknown> = {
            file_name: file_obj.name,
            file_mime_type: file_obj.type,
            type: new_asset.type,
            metadata: this._getMergedMetadata(file_with_metadata.metadata),
            ...this.#analyticsContext,
        };
        if (assetContainerModelIsBackground(new_asset)) {
            eventData['subtype'] = new_asset['background']['type'];

            if (slideBackgroundModelIsVideoBackgroundModel(new_asset.background)) {
                eventData['has_voiceover'] = new_asset.background.video.has_voiceover;
                eventData['duration'] = new_asset.background.video.duration;
                eventData['preview_url'] = new_asset.background.video.preview_url;
                eventData['captions_language_code'] =
                    new_asset.background.video.captions?.language_code;
                eventData['is_sticker'] =
                    new_asset.background.video.source === SourceEnum.SOURCE_USER_STICKERS;
            } else if (slideBackgroundModelIsImageBackgroundModel(new_asset.background)) {
                eventData['preview_url'] = new_asset.background.image.preview_url;
                eventData['is_sticker'] =
                    new_asset.background.image.source === SourceEnum.SOURCE_USER_STICKERS;
            }
        } else if (assetContainerModelIsAudio(new_asset)) {
            eventData['has_voiceover'] = new_asset.audio.has_voiceover;
            eventData['duration'] = new_asset.audio.duration;
            eventData['captions_language_code'] = new_asset.audio.captions?.language_code;
        }
        analytics.track('Media Upload Succeeded', {
            ...eventData,
            ...uploadStageTimings(uploadSpeedTracker),
        });

        // track upload success in prometheus
        ActivityProfiler.inc(METRIC_UPLOAD_SUCCESS_NAME);
    }

    _get_upload_url(file_obj: File) {
        return new Promise<{
            upload_url: string;
            bucket: string;
            makeChunks: boolean;
            asyncOperationUuid: string;
        }>((resolve, reject) => {
            const makeChunks =
                isVideoMimeType(getFileMimeType(file_obj)) && file_obj.size > this.CHUNK_SIZE;

            const urlType = makeChunks
                ? UploadUrlsRequestTypeEnum.INITIALIZE_MULTIPART_UPLOAD
                : UploadUrlsRequestTypeEnum.SINGLE_REQUEST_UPLOAD;
            ApiService.uploadUrlsCreate({
                body: {
                    file_name: file_obj.name,
                    type: urlType,
                },
            })
                .then((response) => {
                    resolve({
                        upload_url: response.data.upload_url,
                        bucket: response.data.bucket,
                        makeChunks,
                        asyncOperationUuid: response.data.async_operation_uuid,
                    });
                })
                .catch((e) => {
                    reject(new ApiRequestError(e, urlType));
                });

            // also remember to update the progress when we are done this call
            this.progress_dict[file_obj.name] = this.UPLOAD_URL_PROGRESS;
            this._trigger_progress_callback();
        });
    }

    _upload_file_to_gcs(file_obj: AugmentedFile, upload_url: string, makeChunks: boolean) {
        if (makeChunks) {
            return this._upload_file_to_gcs_as_chunks(file_obj, upload_url);
        } else {
            return this._upload_file_to_gcs_as_single_blob(file_obj, upload_url);
        }
    }

    async _upload_file_to_gcs_as_single_blob(file_obj: AugmentedFile, upload_url: string) {
        const uploadNetworkManager = getGlobalUploadNetworkManager();
        return uploadNetworkManager.queueTask(async () => {
            return new Promise<{ file_name: string }>((resolve, reject) => {
                if (this.abort_controller.signal.aborted) {
                    reject(new OperationAbortedError('Upload canceled by user'));
                    return;
                }

                const xhr = this._get_xhr_with_progress((progress_percent) => {
                    this.progress_dict[file_obj.name] =
                        this.UPLOAD_URL_PROGRESS +
                        (progress_percent * this.GCS_UPLOAD_PROGRESS) / 100.0;
                    this._trigger_progress_callback();
                });

                // XMLHttpRequest has no `signal` option, so an in-flight upload is only
                // stoppable by calling xhr.abort() ourselves in response to the signal.
                const onAbortSignal = () => xhr.abort();
                this.abort_controller.signal.addEventListener('abort', onAbortSignal);

                xhr.open('PUT', upload_url);
                xhr.onload = () => {
                    this.abort_controller.signal.removeEventListener('abort', onAbortSignal);
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve({ file_name: getFileNameFromUploadUrl(upload_url) });
                    } else {
                        ErrorReporter.capture(xhr);
                        reject(
                            new GCSUploadError(
                                `File upload failed (status ${xhr.status}): please connect to stable internet and try again.`,
                            ),
                        );
                    }
                };

                xhr.onerror = (e) => {
                    this.abort_controller.signal.removeEventListener('abort', onAbortSignal);
                    // corporate firewalls sometimes block this request
                    reject(
                        new GCSUploadError(
                            'File upload failed: please connect to stable internet and retry. If you consistently have this error and are connected to a corporate network, please speak with your IT department.',
                        ),
                    );
                };

                xhr.onabort = () => {
                    this.abort_controller.signal.removeEventListener('abort', onAbortSignal);
                    reject(new OperationAbortedError('Upload canceled by user'));
                };

                xhr.send(file_obj);
            });
        });
    }

    async #generateSignedUrl(
        urlType: UploadUrlsRequestTypeEnum,
        otherData: Omit<UploadUrlsRequest, 'type'>,
    ) {
        return new Promise<string>((resolve, reject) => {
            ApiService.uploadUrlsCreate({
                body: {
                    type: urlType,
                    ...otherData,
                },
            })
                .then((response) => {
                    resolve(response.data.upload_url);
                })
                .catch((e) => {
                    ErrorReporter.capture(e);
                    reject(new ApiRequestError(e, urlType));
                });
        });
    }

    async #initiateMultipartUpload(upload_url: string) {
        let responseText;
        try {
            const response = await fetch(upload_url, {
                method: 'POST',
                signal: this.abort_controller.signal,
            });
            responseText = await response.text();
        } catch (error) {
            if (this.abort_controller.signal.aborted) {
                throw new OperationAbortedError('Upload canceled by user');
            }
            throw new GCSUploadError('Error initiating upload session.');
        }

        let uploadId, bucket, uploadFileName;
        try {
            const xmlParser = new window.DOMParser();
            const responseData = xmlParser.parseFromString(responseText, 'text/xml');
            uploadId = responseData.getElementsByTagName('UploadId')[0].textContent;
            bucket = responseData.getElementsByTagName('Bucket')[0].textContent;
            uploadFileName = responseData.getElementsByTagName('Key')[0].textContent;
        } catch (error) {
            ErrorReporter.capture(error);
        }

        // `bucket` is validated as a well-formed-response sanity check but not
        // returned: the bucket the upload is reported against comes from the
        // signed-URL response, not the GCS multipart XML.
        if (!uploadId || !bucket || !uploadFileName) {
            throw new UploadError(
                ErrorTypes.REQUEST_ERROR,
                'Error starting up the upload session.',
            );
        }

        return { uploadId, uploadFileName };
    }

    #createChunks(file_obj: AugmentedFile) {
        let start = 0;
        const chunks = [] as FileChunk[];
        do {
            const end = Math.min(start + this.CHUNK_SIZE, file_obj.size);
            const chunk = file_obj.slice(start, end);
            chunks.push({
                chunk,
                start,
                end,
            });
            start = end;
        } while (start < file_obj.size);
        return chunks;
    }

    #uploadAllChunks(
        chunkList: FileChunk[],
        uploadId: string,
        uploadFileName: string,
        totalFileSize: number,
        progressDictKey: string,
    ) {
        const uploadNetworkManager = getGlobalUploadNetworkManager();
        const promiseList = chunkList.map(async (chunkData, index) => {
            const partNumber = index + 1; // part number is 1-indexed rather than 0-indexed, so we must add 1 so that the parts are 1, 2, 3, ...
            const uploadUrl = await this.#generateSignedUrl(
                UploadUrlsRequestTypeEnum.PART_OF_MULTIPART_UPLOAD,
                {
                    upload_id: uploadId,
                    file_name: uploadFileName,
                    part_number: partNumber,
                },
            );
            return uploadNetworkManager.queueTask(async () => {
                let response = null;
                try {
                    response = await fetch(uploadUrl, {
                        method: 'PUT',
                        headers: {
                            'Content-Range': `bytes ${chunkData.start}-${chunkData.end - 1}/${totalFileSize}`,
                        },
                        body: chunkData.chunk,
                        signal: this.abort_controller.signal,
                    });
                } catch (error) {
                    if (this.abort_controller.signal.aborted) {
                        throw new OperationAbortedError('Upload canceled by user');
                    }
                    throw new GCSUploadError(
                        'Error uploading file. Please re-connect to stable internet and try again. If you see this message repeatedly and are connected to a corporate network, please speak with your IT department.',
                    );
                }

                if (!response.ok) {
                    ErrorReporter.capture(response);
                    throw new GCSUploadError(
                        `Error uploading file (status ${response.status}). Please re-connect to stable internet and try again.`,
                    );
                }

                this.progress_dict[progressDictKey] += this.GCS_UPLOAD_PROGRESS / chunkList.length;
                this._trigger_progress_callback();

                return {
                    etag: response.headers.get('etag') || '',
                    partNumber: partNumber,
                };
            });
        });
        return Promise.all(promiseList);
    }

    async #completeMultipartUpload(chunkUploadResults: ChunkResult[], finalizeUploadUrl: string) {
        const xml = `<CompleteMultipartUpload>${chunkUploadResults
            .map((result) => {
                return `<Part><PartNumber>${result.partNumber}</PartNumber><ETag>${result.etag}</ETag></Part>`;
            })
            .join('')}</CompleteMultipartUpload>`;

        let response;
        try {
            response = await fetch(finalizeUploadUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/xml; charset=UTF-8',
                },
                body: xml,
                signal: this.abort_controller.signal,
            });
        } catch (error) {
            if (this.abort_controller.signal.aborted) {
                throw new OperationAbortedError('Upload canceled by user');
            }
            throw new GCSUploadError(
                'Error completing upload. Please re-connect to stable internet and try again.',
            );
        }

        if (!response.ok) {
            ErrorReporter.capture(response);
            throw new GCSUploadError(
                `Error completing upload: ${response.status}. Please re-connect to stable internet and try again.`,
            );
        }
        return response;
    }

    async _upload_file_to_gcs_as_chunks(file_obj: AugmentedFile, upload_url: string) {
        const { uploadId, uploadFileName } = await this.#initiateMultipartUpload(upload_url);
        const chunkList = this.#createChunks(file_obj);
        const chunkUploadResults = await this.#uploadAllChunks(
            chunkList,
            uploadId,
            uploadFileName,
            file_obj.size,
            file_obj.name,
        );
        const completeUploadUrl = await this.#generateSignedUrl(
            UploadUrlsRequestTypeEnum.COMPLETE_MULTIPART_UPLOAD,
            {
                upload_id: uploadId,
                file_name: uploadFileName,
            },
        );
        await this.#completeMultipartUpload(chunkUploadResults, completeUploadUrl);
        return {
            file_name: uploadFileName,
        };
    }

    _get_xhr_with_progress(progress_callback: (progress: number) => void) {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
                const percent_complete = (e.loaded * 100.0) / e.total;
                progress_callback(percent_complete);
            }
        });

        // report 100% finished when "load" happens
        xhr.addEventListener('load', () => {
            progress_callback(100);
        });

        // report 100% finished when "abort" happens
        xhr.addEventListener('abort', () => {
            progress_callback(100);
        });
        return xhr;
    }

    _getMergedMetadata(fileMetadata: FileMetadata = {}): AssetMetadataRequest {
        const metadata = {} as AssetMetadataRequest;
        // merge metadata from the controller
        if (this.metadata) {
            if (isNotUndefined(this.metadata.uuid)) {
                metadata.uuid = this.metadata.uuid;
            }
            if (isNotUndefined(this.metadata.source)) {
                metadata.source = this.metadata.source;
            }
            if (isNotUndefined(this.metadata.license)) {
                metadata.license = this.metadata.license;
            }
            if (isNotUndefined(this.metadata.language)) {
                metadata.language = this.metadata.language;
            }
            if (isNotUndefined(this.metadata.start_millis)) {
                metadata.start_millis = this.metadata.start_millis;
            }
            if (isNotUndefined(this.metadata.end_millis)) {
                metadata.end_millis = this.metadata.end_millis;
            }
            if (isNotUndefined(this.metadata.script)) {
                metadata.script = this.metadata.script;
            }
            if (isNotUndefined(this.metadata.ai_voice)) {
                metadata.ai_voice = this.metadata.ai_voice;
            }
            if (isNotUndefined(this.metadata.article_id)) {
                metadata.article_id = this.metadata.article_id;
            }
            if (isNotUndefined(this.metadata.document_id)) {
                metadata.document_id = this.metadata.document_id;
            }
            if (isNotUndefined(this.metadata.script_with_word_timings)) {
                metadata.script_with_word_timings = this.metadata.script_with_word_timings;
            }
            if (isNotUndefined(this.metadata.description)) {
                metadata.description = this.metadata.description;
            }
            if (isNotUndefined(this.metadata.generation_uuid)) {
                metadata.generation_uuid = this.metadata.generation_uuid;
            }
            if (isNotUndefined(this.metadata.source_id)) {
                metadata.source_id = this.metadata.source_id;
            }
            if (isNotUndefined(this.metadata.fallback_to_music_if_no_voiceover)) {
                metadata.fallback_to_music_if_no_voiceover =
                    this.metadata.fallback_to_music_if_no_voiceover;
            }
        }
        // merge metadata from the file
        if (fileMetadata) {
            if (isNotUndefined(fileMetadata.source)) {
                metadata.source = fileMetadata.source;
            }
            if (isNotUndefined(fileMetadata.license)) {
                metadata.license = fileMetadata.license;
            }
            if (isNotUndefined(fileMetadata.language)) {
                metadata.language = fileMetadata.language;
            }
            if (isNotUndefined(fileMetadata.start_millis)) {
                metadata.start_millis = fileMetadata.start_millis;
            }
            if (isNotUndefined(fileMetadata.end_millis)) {
                metadata.end_millis = fileMetadata.end_millis;
            }
            if (isNotUndefined(fileMetadata.script)) {
                metadata.script = fileMetadata.script;
            }
            if (isNotUndefined(fileMetadata.ai_voice)) {
                metadata.ai_voice = fileMetadata.ai_voice;
            }
            if (isNotUndefined(fileMetadata.article_id)) {
                metadata.article_id = fileMetadata.article_id;
            }
            if (isNotUndefined(fileMetadata.document_id)) {
                metadata.document_id = fileMetadata.document_id;
            }
            if (isNotUndefined(fileMetadata.can_background_transcode)) {
                metadata.can_background_transcode = fileMetadata.can_background_transcode;
            }
            if (isNotUndefined(fileMetadata.from_clip_creator)) {
                metadata.from_clip_creator = fileMetadata.from_clip_creator;
            }
            if (isNotUndefined(fileMetadata.fallback_to_music_if_no_voiceover)) {
                metadata.fallback_to_music_if_no_voiceover =
                    fileMetadata.fallback_to_music_if_no_voiceover;
            }
        }
        // return merged asset metadata
        return metadata;
    }

    async _trigger_validation(
        file_obj: AugmentedFile,
        bucket: string,
        file_name: string,
        fileMetadata: FileMetadata | undefined,
        asyncOperationUuid: string,
    ) {
        return new Promise<void>((resolve, reject) => {
            ApiService.assetValidationUpdate({
                body: {
                    metadata: this._getMergedMetadata(fileMetadata),
                    bucket,
                    file_name,
                    add_to_user_library: this.add_to_user_library,
                    only_images: this.only_images,
                    user_file_name: file_obj.name,
                    async_operation_uuid: asyncOperationUuid,
                },
            })
                .then(() => {
                    resolve();
                })
                .catch((e) => {
                    ErrorReporter.capture(e);
                    reject(new ApiRequestError(e, OtherRequestTypes.TRIGGERING_VALIDATION));
                });
        });
    }

    _trigger_progress_callback() {
        // call this function whenever there is a progress event
        // that should be reported to handling code.
        let file_count = 0;
        let cumulative_progress = 0.0;
        for (const key in this.progress_dict) {
            file_count++;
            cumulative_progress += this.progress_dict[key];
        }

        let averaged_progress = 0.0;
        if (cumulative_progress > 0.1 && file_count > 0) {
            averaged_progress = (cumulative_progress * 1.0) / file_count;
        }

        if (averaged_progress >= 99.9) {
            averaged_progress = -1; // make the progress bar disappear
        }
        this.progress_callback(averaged_progress, this.session_id, { ...this.progress_dict });
    }

    _is_valid_file(fileWithMetadata: FileWithMetadata) {
        const file_obj = fileWithMetadata.file;
        return new Promise<FileMetadata | undefined>((resolve, reject) => {
            // check the file size
            const fileSize = file_obj.size;

            // file too small
            if (fileSize <= Number(UploadFileSizeRequirements.MIN_BYTES)) {
                reject(new FileError(ERROR_MESSAGES.FILE_SIZE_TOO_SMALL));
            }

            // file too big
            if (fileSize >= Number(UploadFileSizeRequirements.MAX_BYTES)) {
                reject(new FileError(ERROR_MESSAGES.FILE_SIZE_TOO_BIG));
            }

            // check mime type
            const mimeType = getFileMimeType(file_obj);
            const allowedFileFormats = this.#buildAllowedFileFormatsList();
            const allowedMimeTypes = getMimeTypesForFileFormats(allowedFileFormats);
            const allowedExtensions = getDisplayNamesForFileFormats(allowedFileFormats);
            const errorMessage = `Must be ${allowedExtensions.join(', ')}.`;
            if (!allowedMimeTypes.includes(mimeType)) {
                reject(new FileError(errorMessage));
            }

            const is_valid_image = isImageMimeType(mimeType, this.only_images);
            const is_valid_video = isVideoMimeType(mimeType);
            const is_valid_audio =
                isAudioMimeType(mimeType) ||
                (is_valid_video && this.#acceptsVideoContainersAsAudio());

            if (this.asset_type === AssetContainerTypes.FONT) {
                const fr = new FileReader();
                fr.onload = (e: ProgressEvent<FileReader>) => {
                    const target = e.target;
                    if (!target) {
                        reject(new FileError(ERROR_MESSAGES.FILE_LOAD_ERROR));
                        return;
                    }

                    let signature = '';
                    const arr = new Uint8Array(e.target.result as ArrayBuffer).subarray(0, 4);
                    for (let i = 0; i < arr.length; i++) {
                        signature += String.fromCharCode(arr[i]);
                    }

                    // Check the file signature against known ttf and otf signatures
                    switch (signature) {
                        case String.fromCharCode(0, 1, 0, 0):
                        case 'true':
                        case 'typ1':
                        case 'OTTO':
                            resolve(fileWithMetadata.metadata);
                            break;
                        default:
                            reject(new FileError(errorMessage));
                    }
                };
                fr.onerror = () => {
                    reject(new FileError(ERROR_MESSAGES.FILE_LOAD_ERROR));
                };
                fr.readAsArrayBuffer(file_obj);
            } else if (is_valid_image && this.asset_type === AssetContainerTypes.BACKGROUND) {
                const fr = new FileReader();
                fr.onload = () => {
                    const img = new Image();
                    img.onload = async () => {
                        if (img.width < this.min_width || img.height < this.min_height) {
                            reject(
                                new FileError(
                                    ERROR_MESSAGES.IMG_DIMENSIONS_TOO_SMALL(
                                        this.min_width,
                                        this.min_height,
                                    ),
                                ),
                            );
                            return;
                        }
                        if (img.width > this.max_width || img.height > this.max_height) {
                            try {
                                const resizedFile = await resizeImageToFit(
                                    file_obj,
                                    img,
                                    this.max_width,
                                    this.max_height,
                                );
                                this.#resizedUploadBlobs.set(file_obj, resizedFile);
                            } catch (e) {
                                ErrorReporter.capture(e);
                                reject(new FileError(ERROR_MESSAGES.IMG_LOAD_ERROR));
                                return;
                            }
                        }
                        resolve(fileWithMetadata.metadata);
                    };
                    img.onerror = () => {
                        reject(new FileError(ERROR_MESSAGES.IMG_LOAD_ERROR));
                    };
                    img.src = fr.result as string;
                };
                fr.readAsDataURL(file_obj);
            } else if (
                is_valid_video &&
                !this.only_images &&
                this.asset_type === AssetContainerTypes.BACKGROUND
            ) {
                const url = (URL || webkitURL).createObjectURL(file_obj);
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.onloadedmetadata = async () => {
                    // we need to track video duration for valid and invalid files
                    if (!isFinite(video.duration)) {
                        await validateInfinityDuration(video);
                    }
                    analytics.track('Pre-upload duration tracker', {
                        file_name: file_obj.name,
                        duration: video.duration * 1000, // milliseconds
                    });

                    // if either size is zero, perhaps pre-loading didn't work, so let the backend try anyway
                    if (
                        (video.videoWidth < this.min_width && video.videoWidth !== 0) ||
                        (video.videoHeight < this.min_height && video.videoHeight !== 0)
                    ) {
                        (URL || webkitURL).revokeObjectURL(url);
                        reject(
                            new FileError(
                                ERROR_MESSAGES.VIDEO_DIMENSIONS_TOO_SMALL(
                                    this.min_width,
                                    this.min_height,
                                ),
                            ),
                        );
                        return;
                    }
                    if (video.videoWidth > this.max_width || video.videoHeight > this.max_height) {
                        (URL || webkitURL).revokeObjectURL(url);
                        reject(
                            new FileError(
                                ERROR_MESSAGES.VIDEO_DIMENSIONS_TOO_BIG(
                                    this.max_width,
                                    this.max_height,
                                ),
                            ),
                        );
                        return;
                    }
                    if (video.duration < Number(BackgroundVideoDurationSeconds.MIN)) {
                        (URL || webkitURL).revokeObjectURL(url);
                        reject(new FileError(ERROR_MESSAGES.VIDEO_DURATION_TOO_SHORT));
                        return;
                    }
                    const newMetadata: FileMetadata = {
                        ...fileWithMetadata.metadata,
                        // if the browser can load the file, we can allow the user to use it before transcoding
                        can_background_transcode: true,
                        duration: video.duration,
                    };
                    // emit the calculated metadata to whoever is listening
                    this.metadata_updated_callback(fileWithMetadata, newMetadata);
                    (URL || webkitURL).revokeObjectURL(url);
                    resolve(newMetadata);
                };
                video.onerror = () => {
                    // We're going to resolve it anyway, because ffmpeg on the backend might be able to make it work
                    (URL || webkitURL).revokeObjectURL(url);
                    resolve(fileWithMetadata.metadata);
                };
                video.src = url;
            } else if (is_valid_audio && this.asset_type === AssetContainerTypes.AUDIO) {
                const fr = new FileReader();
                fr.onload = (e: ProgressEvent<FileReader>) => {
                    const target = e.target;
                    if (!target) {
                        reject(new FileError(ERROR_MESSAGES.FILE_LOAD_ERROR));
                        return;
                    }
                    const blob = new Blob([target.result as ArrayBuffer], {
                        type: file_obj.type,
                    });
                    const url = (URL || webkitURL).createObjectURL(blob);
                    const audio = document.createElement('audio');
                    audio.preload = 'metadata';

                    audio.onloadedmetadata = () => {
                        getBlobDuration(url).then((duration) => {
                            if (duration < Number(AudioDurationSeconds.MIN)) {
                                reject(new FileError(ERROR_MESSAGES.AUDIO_DURATION_TOO_SHORT));
                                (URL || webkitURL).revokeObjectURL(url);
                                return;
                            }
                            if (duration > Number(AudioDurationSeconds.MAX)) {
                                // NOTE this limit was added because in rendering (Web Audio API in Chrome)
                                // we were having chrome crash with large audio files.
                                reject(new FileError(ERROR_MESSAGES.AUDIO_DURATION_TOO_LONG));
                                (URL || webkitURL).revokeObjectURL(url);
                                return;
                            }
                            // emit the calculated duration to whoever is listening
                            this.metadata_updated_callback(fileWithMetadata, {
                                duration: duration,
                            });
                            resolve(fileWithMetadata.metadata);
                        });
                    };
                    audio.onerror = (e) => {
                        if (typeof e === 'string') {
                            ErrorReporter.addBreadcrumb({
                                message: e,
                            });
                        } else {
                            ErrorReporter.addBreadcrumb(e);
                        }
                        (URL || webkitURL).revokeObjectURL(url);
                        reject(new FileError(ERROR_MESSAGES.GENERIC_INVALID_UPLOAD_ERROR));
                    };
                    audio.src = url;
                };
                fr.readAsArrayBuffer(file_obj);
            } else if (this.asset_type === AssetContainerTypes.ICON) {
                const fr = new FileReader();
                fr.onload = (e: ProgressEvent<FileReader>) => {
                    const target = e.target;
                    if (!target) {
                        reject(new FileError(ERROR_MESSAGES.FILE_LOAD_ERROR));
                        return;
                    }
                    if ((target.result as string).indexOf('</svg>') === -1) {
                        try {
                            const json = JSON.parse(e.target.result as string);
                            if (json['layers'] === undefined) {
                                reject(new FileError(errorMessage));
                            }
                        } catch (ex) {
                            reject(new FileError(errorMessage));
                        }
                    }
                    resolve(fileWithMetadata.metadata);
                };
                fr.onerror = () => {
                    reject(new FileError(ERROR_MESSAGES.FILE_LOAD_ERROR));
                };
                fr.readAsText(file_obj);
            } else if (this.asset_type === AssetContainerTypes.DOCUMENT) {
                resolve(fileWithMetadata.metadata);
            } else {
                reject(new FileError(errorMessage));
            }
        });
    }

    /**
     * A voiceover is only ever used for its audio track, so a voiceover upload also takes video
     * containers (a Teams or Zoom recording exported as .mp4, with or without a video stream);
     * the backend keeps just the audio.
     */
    #acceptsVideoContainersAsAudio(): boolean {
        return (
            this.asset_type === AssetContainerTypes.AUDIO &&
            this.metadata.source === SourceEnum.SOURCE_USER_VOICEOVER
        );
    }

    #buildAllowedFileFormatsList(): FileFormat[] {
        const fileFormats: FileFormat[] = [];
        if (this.#isElementUpload) {
            fileFormats.push(...getValidImageFileFormats(this.only_images));
            fileFormats.push(...getValidIconFileFormats());
        } else {
            switch (this.asset_type) {
                case AssetContainerTypes.BACKGROUND:
                    fileFormats.push(...getValidImageFileFormats(this.only_images));
                    fileFormats.push(...getValidVideoFileFormats());
                    break;
                case AssetContainerTypes.AUDIO:
                    fileFormats.push(...getValidAudioFileFormats());
                    if (this.#acceptsVideoContainersAsAudio()) {
                        fileFormats.push(...getValidVideoContainerFileFormats());
                    }
                    break;
                case AssetContainerTypes.FONT:
                    fileFormats.push(...getValidFontFileFormats());
                    break;
                case AssetContainerTypes.ICON:
                    fileFormats.push(...getValidIconFileFormats());
                    break;
                case AssetContainerTypes.DOCUMENT:
                    fileFormats.push(...getValidDocumentFileFormats());
                    break;
                default:
                    break;
            }
        }
        if (this.#isUserUpload) {
            return getUserUploadableFileFormats(fileFormats);
        }
        return fileFormats;
    }

    _error_received(
        file_with_metadata: FileWithMetadata,
        displayMessage: string,
        originalMessage: string,
        uploadSpeedTracker: UploadSpeedTracker,
        e: Error,
        { skipAnalytics = false }: { skipAnalytics?: boolean } = {},
    ) {
        const { file: file_obj, metadata } = file_with_metadata;
        this.progress_dict[file_obj.name] = 100; // this file is complete, we are giving up!
        this._trigger_progress_callback();
        this.upload_failed_callback(file_with_metadata, displayMessage, e);
        if (skipAnalytics) {
            return;
        }
        const stageFailed =
            Object.keys(uploadSpeedTracker)
                .find(
                    (key) =>
                        uploadSpeedTracker[key as keyof UploadSpeedTracker] ===
                        UploadStageStatus.ERROR,
                )
                ?.replace('Status', '') || 'unknown';
        analytics.track('Media Upload Failed', {
            file_name: file_obj.name,
            file_mime_type: file_obj.type,
            display_message: displayMessage,
            message: originalMessage,
            metadata: this._getMergedMetadata(metadata),
            stage_failed: stageFailed,
            is_sticker: metadata?.source === SourceEnum.SOURCE_USER_STICKERS,
            ...this.#analyticsContext,
            ...uploadStageTimings(uploadSpeedTracker),
        });
    }

    handleErrors(
        e: HandleableError,
        file_with_metadata: FileWithMetadata,
        uploadSpeedTracker: UploadSpeedTracker,
    ) {
        if (e instanceof OperationAbortedError) {
            // The user explicitly canceled this upload. Still settle/clean up via the normal
            // failed-upload callback, but don't report it as a failure - no analytics event
            // and no Prometheus metric, since nothing actually went wrong.
            this._error_received(file_with_metadata, e.message, e.message, uploadSpeedTracker, e, {
                skipAnalytics: true,
            });
            return;
        }
        const { type, message, expected_error } = e;
        switch (type) {
            case ErrorTypes.FILE_ERROR:
            case ErrorTypes.REQUEST_ERROR:
                this._error_received(file_with_metadata, message, message, uploadSpeedTracker, e);
                break;
            case ErrorTypes.TASK_ERROR:
                this._error_received(file_with_metadata, message, message, uploadSpeedTracker, e);
                if (!expected_error) {
                    ActivityProfiler.inc(METRIC_UPLOAD_FAILED_NAME, {
                        error_type: type,
                    }); // track failed upload in prometheus
                }
                break;
            default:
                this._error_received(
                    file_with_metadata,
                    ERROR_MESSAGES.DEFAULT,
                    message,
                    uploadSpeedTracker,
                    e,
                );
                ActivityProfiler.inc(METRIC_UPLOAD_FAILED_NAME, {
                    error_type: type,
                }); // track failed upload in prometheus
                break;
        }
    }
}

export default UploadMediaController;
